/**
 * Tout ce que le client ECRIT sur le serveur, en un seul endroit.
 *
 * POURQUOI CE MODULE EXISTE. Ces neuf appels vivaient dans `client/jeu.ts`,
 * disperses entre trois sections: la pose d'un batiment sauvait a cote du code
 * de construction, l'animal a cote du zoo, le vehicule a cote de la conduite.
 * Ils ne partagent pourtant rien avec ce qui les entoure et tout entre eux: le
 * meme en-tete, le meme `try`/`catch`, la meme regle « une ecriture ratee
 * previent, elle ne fait jamais tomber la partie ».
 *
 * L'interface est etroite a dessein: un jeton et deux rappels. Ce module ne
 * connait ni le HUD, ni la grille de navigation, ni le rendu — il ne sait pas
 * defaire une pose, il DIT qu'elle a ete refusee et le jeu s'en charge.
 */
import type { Animal, AnimalSauve, Batiment, JardinCueilli, Ressource, Vehicule } from '../../shared/types/monde.ts'
import type { Apparence, Besoins } from '../../shared/types/player.ts'
import type { Cout, Inventaire } from '../../shared/types/items.ts'
import type { Vec2 } from '../../shared/types/world.ts'
import type { ProgresEcole } from '../../shared/sim/ecole/types.ts'
import { validerVehicule } from '../../shared/sim/vehicules.ts'
import type { Dessin, Trait } from '../../shared/sim/dessin.ts'
import { validerDessin } from '../../shared/sim/dessin.ts'
import { journal } from '../log/journal.ts'

export interface EtatSauve {
  token: string
  nom: string
  apparence: Apparence
  inventaire: Inventaire
  besoins: Besoins
  pos: Vec2
  tempsJeu: number
}

/** Ce que le musee rend apres une tentative d'accrochage. */
export interface ReponseAccrochage {
  /** Le dessin tel que le SERVEUR l'a ecrit, ou null s'il a refuse. */
  dessin: Dessin | null
  /** La raison du refus, affichable telle quelle. Vide si le serveur s'est taru. */
  raison: string
}

export interface RappelsSauvegarde {
  /** Previent le joueur. Le module n'a pas d'avis sur la forme du message. */
  message(texte: string): void
  /**
   * Le serveur a refuse cette pose: au jeu de la defaire et de rendre le cout.
   * Le module ne touche a rien du monde, il ne fait que rapporter.
   */
  batimentRefuse(b: Batiment, raison: string): void
}

export class Sauvegarde {
  private readonly token: string
  private readonly rappels: RappelsSauvegarde
  /** Sauvegardes consecutives en echec, pour prevenir le joueur. */
  private echecs = 0

  constructor(token: string, rappels: RappelsSauvegarde) {
    this.token = token
    this.rappels = rappels
  }

  /**
   * En-tetes d'une ecriture dans le monde.
   *
   * Le jeton dit au serveur QUI ecrit, ce dont il se sert pour n'annoncer le
   * changement qu'aux AUTRES: se le voir renvoyer ferait poser deux fois la meme
   * maison. Le meme en-tete sert deja aux routes d'administration.
   */
  private entete(): Record<string, string> {
    return { 'content-type': 'application/json', 'x-jeu-token': this.token }
  }

  async joueur(s: EtatSauve): Promise<void> {
    try {
      const r = await fetch('/api/joueur', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(s),
      })
      // Un 500 n'est pas une erreur pour `fetch`: sans ce controle, on croyait
      // sauvegarder pendant une heure avant de tout perdre en fermant l'onglet.
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      // L'HEURE DU MONDE N'EST PLUS POUSSEE D'ICI. Elle appartient au serveur,
      // qui la fait avancer tout seul et la diffuse (voir server/simulation.ts).
      // Trois clients qui poussaient trois heures differentes, c'etait la
      // quatrieme faiblesse de l'etape un du multijoueur.
      if (this.echecs > 0) {
        journal.info('sauvegarde', `retablie apres ${this.echecs} echecs`)
        this.echecs = 0
      }
    } catch (e) {
      this.echecs++
      journal.warn('sauvegarde', `echec ${this.echecs}: ${String(e)}`)
      // On previent le joueur plutot que de le laisser jouer pour rien.
      if (this.echecs === 3 || this.echecs % 20 === 0) {
        this.rappels.message('⚠ Sauvegarde impossible : le serveur ne répond pas.')
      }
    }
  }

  /**
   * Sauvegarde de derniere chance, au moment ou l'onglet se ferme. `fetch` n'est
   * pas garanti de partir a ce moment: `sendBeacon` si, lui.
   */
  avantFermeture(s: EtatSauve): void {
    const corps = JSON.stringify(s)
    const envoye = navigator.sendBeacon?.('/api/joueur', new Blob([corps], { type: 'text/plain' }))
    if (!envoye) void fetch('/api/joueur', { method: 'POST', body: corps, keepalive: true }).catch(() => {})
    // Plus d'heure du monde ici: elle appartient au serveur. Un onglet qui se
    // fermait en retard pouvait faire sauter une nuit a tous les autres.
  }

  /**
   * DEMANDE UN MOUVEMENT DU SAC, et rend ce que le serveur a ecrit.
   *
   * Le client n'annonce plus de TOTAL: il dit ce qu'il vient de faire, le
   * serveur applique la regle de `shared/` — des compris — et renvoie le sac.
   * C'etait le dernier trou de l'autorite: `POST /api/joueur` recopiait
   * l'inventaire tel quel, donc un onglet trafique s'ecrivait mille minerais.
   *
   * Rend `null` quand le serveur refuse ou ne repond pas: l'appelant garde
   * alors son sac et le DIT au joueur, plutot que d'inventer un gain que
   * personne d'autre ne verra.
   */
  async sac(geste: Record<string, unknown>): Promise<{ inventaire: Inventaire; gains: Cout } | null> {
    try {
      const r = await fetch('/api/sac', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify(geste),
      })
      const rep = (await r.json().catch(() => ({}))) as {
        inventaire?: unknown
        gains?: unknown
        raison?: unknown
      }
      if (!r.ok) {
        journal.warn('sac', `refuse: ${typeof rep.raison === 'string' ? rep.raison : `HTTP ${r.status}`}`)
        return null
      }
      return {
        inventaire: (rep.inventaire ?? {}) as Inventaire,
        gains: (rep.gains ?? {}) as Cout,
      }
    } catch (e) {
      journal.warn('sac', `mouvement perdu: ${String(e)}`)
      return null
    }
  }

  /**
   * Persiste une construction — et previent si le serveur la refuse.
   *
   * Depuis que le serveur rejoue `validerPlacement`, une pose peut echouer alors
   * que le client la croyait bonne: le cas normal est deux joueurs qui visent le
   * meme coin de clairiere a une seconde d'intervalle. Sans ce retour en
   * arriere, le perdant garderait chez lui une maison que personne d'autre ne
   * voit, et qu'il ne pourrait meme pas demolir puisqu'elle n'existe pas en base.
   */
  async batiment(b: Batiment): Promise<Inventaire | null> {
    try {
      const r = await fetch('/api/batiment', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify(b),
      })
      const rep = (await r.json().catch(() => ({}))) as Record<string, unknown>
      // C'EST LE SERVEUR QUI DEBITE. Il rend le sac d'apres la pose: le client
      // l'adopte au lieu de soustraire le cout de son cote, et les deux ne
      // peuvent pas diverger — y compris quand la pose est refusee, ou rien
      // n'a ete pris.
      if (r.ok) return (rep['inventaire'] ?? null) as Inventaire | null
      this.rappels.batimentRefuse(b, String(rep['erreur'] ?? ''))
      return null
    } catch (e) {
      journal.warn('sauvegarde', `batiment non sauve: ${String(e)}`)
      return null
    }
  }

  /** Demolit, et rend le sac REMBOURSE par le serveur (ou null s'il n'a pas repondu). */
  /**
   * Demande la demolition. Rend le sac du serveur, ou `null` si la demolition
   * n'a PAS eu lieu (refus, reseau): l'appelant doit alors remettre le
   * batiment, sinon il disparait d'un seul ecran et revient au rechargement.
   */
  async supprimerBatiment(id: string): Promise<{ inventaire: Inventaire | null } | null> {
    try {
      const r = await fetch(`/api/batiment/${encodeURIComponent(id)}?token=${encodeURIComponent(this.token)}`, {
        method: 'DELETE',
      })
      if (!r.ok) {
        journal.warn('demolir', `demolition refusee par le serveur: HTTP ${r.status}`, { id })
        return null
      }
      const rep = (await r.json().catch(() => ({}))) as Record<string, unknown>
      return { inventaire: (rep['inventaire'] ?? null) as Inventaire | null }
    } catch (e) {
      journal.warn('demolir', `suppression non enregistree cote serveur: ${String(e)}`)
      return null
    }
  }

  /**
   * Persiste la cueillette d'un jardin.
   *
   * L'echeance part en temps de JEU ABSOLU, sur l'horloge que le SERVEUR tient
   * et diffuse: c'est ce qui lui garde son sens apres un redemarrage, et ce qui
   * fait qu'un jardin repousse au meme moment pour tout le monde. Un « dans une
   * journee » relatif se serait decale a chaque relecture.
   */
  async jardin(j: JardinCueilli): Promise<void> {
    try {
      await fetch('/api/jardin', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify(j),
      })
    } catch (e) {
      journal.warn('sauvegarde', `jardin non sauve: ${String(e)}`)
    }
  }

  /**
   * Persiste le progres scolaire du COMPTE.
   *
   * Il vivait dans le stockage local du navigateur: changer de telephone, ou
   * simplement vider son cache, effacait des mois de points. Le serveur le
   * range maintenant par jeton, comme la sauvegarde du bonhomme.
   *
   * Un echec ne previent PAS le joueur et ne defait rien: le stockage local a
   * deja ete ecrit par l'appelant, et il fait office de secours hors ligne. On
   * repartira du plus avance des deux au prochain demarrage.
   */
  async ecole(p: ProgresEcole): Promise<void> {
    try {
      const r = await fetch('/api/ecole', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify({ progres: p }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    } catch (e) {
      journal.warn('ecole', `progres non enregistre cote serveur: ${String(e)}`)
    }
  }

  /**
   * Relit le progres scolaire du compte. `null` quand le serveur ne repond pas,
   * ou quand ce compte n'a jamais joue — l'appelant retombe alors sur ce que le
   * navigateur avait garde.
   *
   * SEULE LECTURE DE CE MODULE, et c'est assume: elle emprunte la meme route,
   * le meme en-tete et la meme regle que l'ecriture d'a cote. La sortir d'ici
   * pour respecter le titre du fichier aurait remis un `fetch` dans le jeu.
   */
  async lireEcole(): Promise<ProgresEcole | null> {
    try {
      const r = await fetch('/api/ecole', { headers: { 'x-jeu-token': this.token } })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const lu = (await r.json()) as { progres?: ProgresEcole | null }
      return lu.progres ?? null
    } catch (e) {
      journal.warn('ecole', `progres non relu depuis le serveur: ${String(e)}`)
      return null
    }
  }

  async ressource(r: Ressource): Promise<void> {
    try {
      await fetch('/api/ressource', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify({ ressourceId: r.id, pv: r.pv, repousseA: r.repousseA }),
      })
    } catch (e) {
      journal.warn('sauvegarde', `ressource non sauvee: ${String(e)}`)
    }
  }

  /** Pose un vehicule. Rend le sac que le SERVEUR a debite, ou null. */
  /**
   * Enregistre un vehicule (neuf ou range). Distingue l'ECHEC de la reussite:
   * un range reussi n'a pas d'inventaire a rendre, et rendre `null` dans les
   * deux cas laissait l'appelant croire a un succes — l'atterrissage refuse
   * d'un avion emprunte disparaissait ainsi sans un mot.
   */
  async vehicule(v: Vehicule): Promise<{ ok: true; inventaire: Inventaire | null } | { ok: false; erreur: string; inventaire: Inventaire | null }> {
    try {
      const r = await fetch('/api/vehicule', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify(v),
      })
      const rep = (await r.json().catch(() => ({}))) as Record<string, unknown>
      const inventaire = (rep['inventaire'] ?? null) as Inventaire | null
      if (!r.ok) {
        const erreur = typeof rep['erreur'] === 'string' ? rep['erreur'] : `HTTP ${r.status}`
        journal.warn('vehicule', `refuse par le serveur: ${erreur}`, { id: v.id })
        return { ok: false, erreur, inventaire }
      }
      return { ok: true, inventaire }
    } catch (e) {
      journal.warn('vehicule', `non enregistre cote serveur: ${String(e)}`)
      return { ok: false, erreur: 'Le serveur ne répond pas.', inventaire: null }
    }
  }

  /**
   * Demande au serveur de verrouiller ou deverrouiller un vehicule.
   *
   * Rend le vehicule TEL QUE LE SERVEUR L'A ECRIT, ou null s'il a refuse (ce
   * n'est pas le notre, la liaison est coupee). C'est volontairement le seul
   * chemin: le verrou protege des autres joueurs, donc il ne veut rien dire
   * tant qu'il n'a pas ete accepte par la seule machine que tout le monde
   * partage.
   */
  async verrouillerVehicule(id: string, verrouille: boolean): Promise<Vehicule | null> {
    try {
      const r = await fetch(`/api/vehicule/${encodeURIComponent(id)}/verrou`, {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify({ verrouille }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const rep = (await r.json()) as { vehicule?: unknown }
      return validerVehicule(rep.vehicule)
    } catch (e) {
      journal.warn('vehicule', `verrou non applique: ${String(e)}`)
      return null
    }
  }

  /**
   * Les dessins accroches au musee.
   *
   * Rend une liste VIDE en cas de panne, jamais une exception: le musee doit
   * s'ouvrir sur des murs nus plutot que de ne pas s'ouvrir. Les dessins sont
   * relus un par un par `validerDessin` — le meme code que le serveur — parce
   * qu'ils viennent du reseau et qu'un seul dessin abime ne doit pas empecher
   * de voir les autres.
   */
  async lireDessins(): Promise<Dessin[]> {
    try {
      const r = await fetch('/api/dessins', { headers: this.entete() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const rep = (await r.json()) as { dessins?: unknown }
      if (!Array.isArray(rep.dessins)) return []
      const sortie: Dessin[] = []
      for (const brut of rep.dessins) {
        const d = validerDessin(brut)
        if (d) sortie.push(d)
      }
      return sortie
    } catch (e) {
      journal.warn('musee', `galerie illisible: ${String(e)}`)
      return []
    }
  }

  /**
   * UN dessin, avec tous ses points.
   *
   * La galerie ne recoit que des versions ALLEGEES (voir `/api/dessins`): assez
   * pour une vignette de 220 pixels, pas pour la regarder en grand. On demande
   * donc le dessin entier au moment ou on l'ouvre — un seul, quand on le
   * regarde vraiment. Rend null si le serveur ne repond pas: l'appelant garde
   * alors la vignette, qui vaut mieux qu'un cadre vide.
   */
  async lireDessin(id: string): Promise<Dessin | null> {
    try {
      const r = await fetch(`/api/dessin/${encodeURIComponent(id)}`, { headers: this.entete() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const rep = (await r.json()) as { dessin?: unknown }
      return validerDessin(rep.dessin)
    } catch (e) {
      journal.warn('musee', `dessin entier illisible: ${String(e)}`)
      return null
    }
  }

  /**
   * Accroche un dessin, et rend celui que le SERVEUR a ecrit.
   *
   * On ne renvoie pas ce qu'on a envoye: c'est le serveur qui signe (l'auteur
   * vient du jeton), qui date (sur l'horloge du monde) et qui calcule
   * l'identifiant. Afficher notre version reviendrait a montrer un dessin qui
   * n'est pas celui que les autres verront.
   */
  async accrocherDessin(titre: string, traits: Trait[]): Promise<ReponseAccrochage> {
    try {
      const r = await fetch('/api/dessin', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify({ titre, traits }),
      })
      const rep = (await r.json().catch(() => ({}))) as { dessin?: unknown; erreur?: unknown }
      if (!r.ok) {
        /*
         * ON REMONTE LA RAISON DU SERVEUR, telle qu'il l'a ecrite.
         *
         * Un invite n'a pas de compte et se voit refuser l'accrochage: sans
         * cette phrase, il lisait « le musee n'a pas pu l'accrocher » et
         * pouvait recommencer indefiniment sans jamais savoir que ce n'est pas
         * une panne. C'est la meme regle que pour le refus d'une pose de
         * batiment — on refuse EN DISANT POURQUOI.
         */
        const raison = typeof rep.erreur === 'string' ? rep.erreur : ''
        journal.warn('musee', `dessin refuse (HTTP ${r.status}): ${raison || 'sans raison'}`)
        return { dessin: null, raison }
      }
      return { dessin: validerDessin(rep.dessin), raison: '' }
    } catch (e) {
      journal.warn('musee', `dessin non accroche: ${String(e)}`)
      return { dessin: null, raison: '' }
    }
  }

  /**
   * Decroche un dessin. Rend vrai si le serveur l'a vraiment retire.
   *
   * Le droit est verifie LA-BAS, dans la clause `WHERE` du DELETE: le
   * navigateur ne fait que proposer le bouton a celui dont le nom est sous la
   * toile, et un client trafique n'obtiendrait rien de plus.
   */
  async decrocherDessin(id: string): Promise<boolean> {
    try {
      const r = await fetch(`/api/dessin/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: this.entete(),
      })
      return r.ok
    } catch (e) {
      journal.warn('musee', `dessin non decroche: ${String(e)}`)
      return false
    }
  }

  async animal(a: Animal): Promise<void> {
    // Un animal qui n'a plus rien de special n'est plus une exception: on
    // efface sa ligne au lieu d'en garder une inutile.
    if (a.repas === 0 && a.ancre === null) {
      try {
        await fetch(`/api/animal/${a.id}?token=${encodeURIComponent(this.token)}`, { method: 'DELETE' })
      } catch (e) {
        journal.warn('zoo', `animal non efface cote serveur: ${String(e)}`)
      }
      return
    }
    const s: AnimalSauve = {
      animalId: a.id,
      kind: a.kind,
      repas: a.repas,
      dernierRepas: a.dernierRepas,
      ami: a.ami ?? '',
      maison: a.maison ?? '',
      pos: { x: a.pos.x, z: a.pos.z },
      ancre: a.ancre ? { x: a.ancre.x, z: a.ancre.z } : null,
      rayon: a.rayon,
    }
    try {
      const r = await fetch('/api/animal', {
        method: 'POST',
        headers: this.entete(),
        body: JSON.stringify(s),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    } catch (e) {
      journal.warn('zoo', `animal non enregistre cote serveur: ${String(e)}`)
    }
  }
}
